import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { stripeApi, authApi } from '../services/api';
import { hasValidSubscription, formatPrice, getTrialDaysRemaining } from '../utils/subscription';
import { Check, Crown, Loader2, Clock, ArrowRight } from 'lucide-react';

interface PricingInfo {
  configured: boolean;
  monthly: { amount: number; currency: string };
  annual: { amount: number; currency: string };
  trialDays: number;
}

// Default pricing to show even if API fails
const DEFAULT_PRICING: PricingInfo = {
  configured: false,
  monthly: { amount: 12, currency: 'usd' },
  annual: { amount: 72, currency: 'usd' },
  trialDays: 14,
};

export default function SubscribeNow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { subscription, setSubscription, addNotification, brandingSettings } = useStore();

  const [pricing, setPricing] = useState<PricingInfo>(DEFAULT_PRICING);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');

  // Check if coming back from successful checkout
  const checkoutStatus = searchParams.get('checkout');

  useEffect(() => {
    loadPricing();

    // Refresh subscription status if coming from checkout
    if (checkoutStatus === 'success') {
      refreshSubscription();
    }
  }, []);

  // Redirect to dashboard if subscription is valid
  useEffect(() => {
    if (hasValidSubscription(subscription)) {
      navigate('/', { replace: true });
    }
  }, [subscription, navigate]);

  const loadPricing = async () => {
    try {
      const data = await stripeApi.getPrices();
      setPricing(data);
    } catch (error) {
      console.error('Failed to load pricing:', error);
      // Keep default pricing on error
    }
  };

  const refreshSubscription = async () => {
    try {
      const { subscription: newSub } = await authApi.getMe();
      setSubscription(newSub);
      if (hasValidSubscription(newSub)) {
        addNotification('Subscription activated!', 'success');
      }
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      await stripeApi.startTrial();
      const { subscription: newSub } = await authApi.getMe();
      setSubscription(newSub);
      addNotification('Your 14-day trial has started!', 'success');
      navigate('/', { replace: true });
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Failed to start trial', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!pricing.configured) {
      addNotification('Payment system not configured yet', 'error');
      return;
    }

    setLoading(true);
    try {
      const { url } = await stripeApi.createCheckoutSession(selectedPlan);
      if (url) {
        window.location.href = url;
      }
    } catch (error: any) {
      addNotification(error.response?.data?.error || 'Failed to start checkout', 'error');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Unlimited document generation',
    'All AI providers (OpenAI, Claude, Gemini)',
    'Cloud storage integrations',
    'Export to PDF, DOCX, HTML',
    'AI image generation',
    'Custom branding & white-label',
    'Priority support',
  ];

  // Show expired trial message if applicable
  const showExpiredMessage = subscription?.status === 'trialing' && getTrialDaysRemaining(subscription) === 0;

  // Calculate annual savings percentage
  const annualSavings = Math.round(((pricing.monthly.amount * 12) - pricing.annual.amount) / (pricing.monthly.amount * 12) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {brandingSettings.companyName || 'DocWriter'}
          </h1>
          {showExpiredMessage ? (
            <p className="text-lg text-red-600">
              Your trial has expired. Subscribe to continue using the app.
            </p>
          ) : (
            <p className="text-lg text-gray-600">
              Choose a plan to get started
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Trial Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="text-blue-500" size={24} />
              <h2 className="text-xl font-semibold">Free Trial</h2>
            </div>

            <p className="text-4xl font-bold text-gray-900 mb-1">
              {pricing.trialDays} days
            </p>
            <p className="text-gray-500 mb-6">No credit card required</p>

            <ul className="space-y-3 mb-6">
              {features.slice(0, 4).map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check size={16} className="text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handleStartTrial}
              disabled={loading}
              className="w-full py-3 px-4 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Starting...
                </>
              ) : (
                <>
                  Start Free Trial
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </div>

          {/* Pro Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-accent relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-accent text-white text-sm font-medium px-3 py-1 rounded-full">
                Recommended
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Crown className="text-accent" size={24} />
              <h2 className="text-xl font-semibold">Pro</h2>
            </div>

            {/* Plan Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  selectedPlan === 'monthly'
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedPlan('annual')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  selectedPlan === 'annual'
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Annual
                <span className="ml-1 text-xs opacity-80">-{annualSavings}%</span>
              </button>
            </div>

            <p className="text-4xl font-bold text-gray-900 mb-1">
              {formatPrice(selectedPlan === 'monthly' ? pricing.monthly.amount : pricing.annual.amount)}
            </p>
            <p className="text-gray-500 mb-6">
              per {selectedPlan === 'monthly' ? 'month' : 'year'}
            </p>

            <ul className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check size={16} className="text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handleSubscribe}
              disabled={loading || !pricing.configured}
              className="w-full py-3 px-4 bg-accent text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Processing...
                </>
              ) : (
                <>
                  Subscribe Now
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            {!pricing.configured && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Payment processing coming soon
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Secure payment powered by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
}
